import type { AbSegment, PageType } from '@/types/nav-types'

export type AbExperimentStatus = 'running' | 'paused' | 'completed'
export type AbExperimentRole = 'control' | 'treatment'

/**
 * Which downfunnel proportion decides the experiment. `long-visits` (the
 * default) is the whole-site "did the searcher stay" measure; `affiliate-
 * conversion` is the money metric, for treatments whose entire job is the
 * click-out — a pricing UI, say — where long visits would be a proxy for
 * nothing. The verdict card and the scoreboard's primary row follow this key.
 */
export type AbPrimaryMetricKey = 'long-visits' | 'affiliate-conversion'

export interface AbExperimentScope {
  label: string
  path: string
  /**
   * `exact` and `prefix` match the pageview path alone. `page-type` matches the
   * path prefix AND the `pageType` property stamped on every event, for a
   * treatment that lives on a whole page type (product pages share the
   * `/hearing-aids/` prefix with brand hubs and articles, so a prefix alone
   * would count visitors who never saw the treatment).
   */
  match: 'exact' | 'prefix' | 'page-type'
  pageType?: PageType
}

export interface AbExperimentVariant {
  segment: AbSegment
  name: string
  description: string
  role: AbExperimentRole
}

export interface AbExperiment {
  id: string
  /** Immutable affiliate attribution key; use a new key for a new test run. */
  trackingKey: string
  name: string
  status: AbExperimentStatus
  startedAt: string
  endedAt?: string
  /** The arm selected for permanent rollout after the experiment concludes. */
  winner?: AbSegment
  hypothesis: string
  primaryMetric: string
  /** Defaults to `long-visits`. See AbPrimaryMetricKey. */
  primaryMetricKey?: AbPrimaryMetricKey
  /**
   * Visitors per arm before the dashboard is allowed to name a winner, fixed in
   * advance from the power calculation. This is the guard against the failure
   * mode this experiment has already produced once: at n = 17 the on-page median
   * gave p = 0.011, and the effect evaporated when three parked tabs were
   * removed. A p-value read off a sample too small to detect a plausible effect
   * is a coin flip with a decimal point, so the verdict card withholds the badge
   * below this number no matter what p says.
   */
  minimumSamplePerArm: number
  assignment: string
  scopes: readonly [AbExperimentScope, ...AbExperimentScope[]]
  variants: Record<AbSegment, AbExperimentVariant>
}

// Shared with the runtime route classifier so the dashboard cannot silently
// describe a different exposure surface than the code serving the treatment.
export const RESOUND_BRAND_GUIDE_PATH = '/hearing-aids/resound'

// The product page's pricing section AND sticky scorecard: seller cards plus
// the tabbed scorecard (control) vs the tier ladder + seller list plus the
// adaptive scorecard (treatment). `product-prices-section.tsx` and
// `product-scorecard.tsx` both pick their variant through `abExperimentArm`,
// so this entry is the single rollout switch for the pair.
export const PRICING_LADDER_EXPERIMENT_ID = 'product-pricing-ladder'

export const BACKGROUND_NOISE_VERDICT_EXPERIMENT_ID = 'background-noise-quick-verdict'
export const BACKGROUND_NOISE_VERDICT_PATH = '/hearing-aids/best-hearing-aids-background-noise'
export const BEST_HEARING_AIDS_VERDICT_EXPERIMENT_ID = 'best-hearing-aids-quick-verdict'
export const BEST_HEARING_AIDS_VERDICT_PATH = '/best-hearing-aids'

export const AB_EXPERIMENTS = [
  {
    id: 'resound-brand-guide',
    trackingKey: 'rsg1',
    name: 'ReSound Brand Guide',
    status: 'completed',
    startedAt: '2026-08-05T00:00:00-05:00',
    endedAt: '2026-08-24T19:43:31-05:00',
    winner: 'a',
    hypothesis:
      'A structured brand hub will drive deeper engagement than the existing editorial listicle on the same URL.',
    // Was 'Engaged visitors' until 2026-08-06. That metric was provably incapable
    // of ever resolving this test — it ran 88.6% / 89.9% during the A/A period, so
    // it is saturated and cannot move far enough to decide anything. Swapping a
    // primary metric mid-flight is normally a statistical sin; it is defensible
    // here only because the original could not resolve and no result had been read
    // yet. Long visits sat at ~49% in the same A/A window, where a proportion is
    // most informative. See docs/decisions/2026-08-06-ab-test-cohort-and-downfunnel.md.
    primaryMetric: 'Long visits (60s+ total time on site)',
    // ~400/arm detects a +20% relative lift on the measured ~49% baseline at 80%
    // power, α = 0.05 two-sided. At the ex-NZ accrual rate of 12.6 visitors/arm/day
    // that is about 32 days. The same +20% on affiliate conversion (~31% baseline)
    // would need ~916/arm, roughly 10 weeks — which is why the test is called on
    // this metric and affiliate conversion is read as the money-side confirmation.
    minimumSamplePerArm: 400,
    assignment: '50/50 persistent visitor split',
    scopes: [
      {
        label: 'ReSound Hearing Aid Guide',
        path: RESOUND_BRAND_GUIDE_PATH,
        match: 'exact',
      },
    ],
    variants: {
      a: {
        segment: 'a',
        name: 'Brand Guide Hub',
        description: 'New hub layout with a structured hero, finder, product lineup, technology levels, and guide.',
        role: 'treatment',
      },
      b: {
        segment: 'b',
        name: 'Existing Listicle',
        description: 'The unchanged editorial article visitors saw before the experiment launched.',
        role: 'control',
      },
    },
  },
  {
    id: PRICING_LADDER_EXPERIMENT_ID,
    trackingKey: 'ppl1',
    name: 'Product Pricing Ladder + Adaptive Scorecard',
    status: 'completed',
    startedAt: '2026-09-10T00:00:00-05:00',
    endedAt: '2026-09-12T21:13:14-05:00',
    winner: 'b',
    hypothesis:
      'Showing technology-level prices and upgrade differences alongside seller care comparisons, with a navy scorecard whose offer follows the selected model and level, will send more product-page visitors out to a seller than the segmented selectors, stacked seller cards, and tabbed scorecard.',
    // The treatment's whole job is the click-out, so the money metric decides it
    // directly; long visits would measure nothing this change is meant to move.
    primaryMetric: 'Affiliate conversion (clicked out to a retailer after exposure)',
    primaryMetricKey: 'affiliate-conversion',
    // Hand-computed for THIS metric, per the follow-up in
    // docs/decisions/2026-08-06-ab-test-cohort-and-downfunnel.md: ~916/arm
    // detects a +20% relative lift on the ~31% post-exposure affiliate baseline
    // at 80% power, α = 0.05 two-sided. Rounded up. Product pages accrue far
    // faster than the single ReSound hub did, so the floor is reachable in weeks.
    minimumSamplePerArm: 1000,
    assignment: '50/50 persistent visitor split',
    scopes: [
      {
        label: 'Hearing aid product pages',
        path: '/hearing-aids/',
        match: 'page-type',
        pageType: 'product',
      },
    ],
    variants: {
      a: {
        segment: 'a',
        name: 'Seller cards + tabbed scorecard',
        description:
          'The original wider pricing section (segmented model and tier selectors, upgrade-highlight chips, seller cards in seller-priority order) and tabbed scorecard in its original sidebar, with a compact seller badge and Learn more footer.',
        role: 'control',
      },
      b: {
        segment: 'b',
        name: 'Tier ladder + adaptive scorecard',
        description:
          'Technology-level price tiles, before/after upgrade differences, seller prices and care comparisons, and a navy scorecard with a fixed tested configuration and a purchase offer synchronized to the selected model and level. The additional detailed hands-on and Sound Lab rating breakdown is deferred from this test.',
        role: 'treatment',
      },
    },
  },
  {
    id: BACKGROUND_NOISE_VERDICT_EXPERIMENT_ID,
    trackingKey: 'bnqv1',
    name: 'Background Noise: Quick Verdict',
    status: 'running',
    // Set to the production deployment time when launching, so the report
    // excludes pre-launch pageviews where both segments still had the card.
    startedAt: '2026-09-20T22:02:13Z',
    hypothesis:
      'Removing the Quick Verdict card will bring readers to the recommendations sooner and increase affiliate conversion without reducing engagement.',
    primaryMetric: 'Affiliate conversion (clicked out to a retailer after exposure)',
    primaryMetricKey: 'affiliate-conversion',
    // Conservative two-proportion sample size: ~1,570/arm detects a 5 percentage
    // point absolute change at 80% power and two-sided alpha .05, using the
    // maximum-variance 50% baseline. Round up; this is not a measured baseline.
    minimumSamplePerArm: 1600,
    assignment: '50/50 persistent visitor split',
    scopes: [
      {
        label: 'Best Hearing Aids for Background Noise (English)',
        path: BACKGROUND_NOISE_VERDICT_PATH,
        match: 'exact',
      },
    ],
    variants: {
      a: {
        segment: 'a',
        name: 'Quick Verdict shown',
        description: 'The existing article with its complete Quick Verdict card above the recommendations.',
        role: 'control',
      },
      b: {
        segment: 'b',
        name: 'Quick Verdict removed',
        description:
          'The same article with the entire Quick Verdict card omitted. The introduction, recommendations, comparison table, and detailed reviews remain.',
        role: 'treatment',
      },
    },
  },
  {
    id: BEST_HEARING_AIDS_VERDICT_EXPERIMENT_ID,
    trackingKey: 'bhaqv1',
    name: 'Best Hearing Aids: Quick Verdict',
    status: 'running',
    // Reporting starts after the launch deployment, excluding pre-launch
    // pageviews where both segments still had the card.
    startedAt: '2026-09-27T10:45:00Z',
    hypothesis:
      'Removing the Quick Verdict card will bring readers to the recommendations sooner and increase affiliate conversion without reducing engagement.',
    primaryMetric: 'Affiliate conversion (clicked out to a retailer after exposure)',
    primaryMetricKey: 'affiliate-conversion',
    // Same conservative floor as the background-noise test: a 5 percentage
    // point absolute change at 80% power and two-sided alpha .05, using the
    // maximum-variance 50% baseline rather than assuming this page's baseline.
    minimumSamplePerArm: 1600,
    assignment: '50/50 persistent visitor split',
    scopes: [
      {
        label: 'Best Hearing Aids (English)',
        path: BEST_HEARING_AIDS_VERDICT_PATH,
        match: 'exact',
      },
    ],
    variants: {
      a: {
        segment: 'a',
        name: 'Quick Verdict shown',
        description: 'The existing article with its complete Quick Verdict card above the recommendations.',
        role: 'control',
      },
      b: {
        segment: 'b',
        name: 'Quick Verdict removed',
        description:
          'The same article with the entire Quick Verdict card omitted. The introduction, recommendations, comparison table, and detailed reviews remain.',
        role: 'treatment',
      },
    },
  },
] as const satisfies readonly AbExperiment[]

export type AbExperimentId = (typeof AB_EXPERIMENTS)[number]['id']

export const findAbExperiment = (id: string): AbExperiment | undefined =>
  AB_EXPERIMENTS.find((experiment) => experiment.id === id)

/**
 * The arm a visitor in `segment` should be served. This is the single rollout
 * switch for code that renders an experiment: while it runs, the segment
 * decides; once it is completed with a recorded winner, everyone gets the
 * winner's arm; paused, unknown, or completed without a winner all fall back
 * to control. Flip the registry entry and the site follows — no component edit.
 */
export const abExperimentArm = (id: string, segment: AbSegment): AbExperimentRole => {
  const experiment = findAbExperiment(id)
  if (!experiment) {
    return 'control'
  }
  if (experiment.status === 'running') {
    return experiment.variants[segment].role
  }
  if (experiment.status === 'completed' && experiment.winner) {
    return experiment.variants[experiment.winner].role
  }
  return 'control'
}
